import { useState, useEffect, forwardRef, useImperativeHandle, useRef } from 'react'
import { toast } from 'sonner'
import { Plus, Trash2 } from 'lucide-react'

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

import {
  useCampaignIntegrations,
  useUpsertIntegration,
  useDeleteIntegration,
  type IntegrationRow,
  type ArgumentConfig,
  type ValueType,
} from '@/entities/campaign'

type VariableEntry = {
  id: string
  key: string
  value: string
  valueType: ValueType
}

// ---------------------------------------------------------------------------
// Field components — matching campaigns page style (MetricsConfigTable)
// ---------------------------------------------------------------------------

function ArgumentField({
  label,
  required,
  description,
  value,
  onValueChange,
  placeholder,
  valueType,
  onValueTypeChange,
}: {
  label: string
  required?: boolean
  description?: string
  value: string
  onValueChange: (v: string) => void
  placeholder?: string
  valueType?: ValueType
  onValueTypeChange?: (v: ValueType) => void
}) {
  return (
    <div className="border rounded-lg p-3 bg-muted/50 space-y-2">
      <div className="flex items-center justify-between gap-2">
        <div className="flex-1 grid gap-1">
          <span className="text-detail font-medium text-gray-600">
            {label}{required && <span className="text-destructive ml-0.5">*</span>}
          </span>
          {description && <p className="text-detail text-gray-500">{description}</p>}
        </div>
        {valueType && onValueTypeChange && (
          <Select value={valueType} onValueChange={(v) => onValueTypeChange(v as ValueType)}>
            <SelectTrigger className="h-8 w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="fixed_value">Fixed Value</SelectItem>
              <SelectItem value="llm_value">LLM Prompt</SelectItem>
            </SelectContent>
          </Select>
        )}
      </div>
      {valueType === 'llm_value' ? (
        <Textarea
          value={value}
          onChange={(e) => onValueChange(e.target.value)}
          placeholder={placeholder ?? 'Analysis key name'}
          className="min-h-[80px]"
        />
      ) : (
        <Input
          value={value}
          onChange={(e) => onValueChange(e.target.value)}
          placeholder={placeholder ?? 'Enter value'}
          className="h-8"
        />
      )}
    </div>
  )
}

function CustomArgumentRow({
  entry,
  onChange,
  onRemove,
  showValueType,
}: {
  entry: VariableEntry
  onChange: (patch: Partial<VariableEntry>) => void
  onRemove: () => void
  showValueType?: boolean
}) {
  return (
    <div className="border rounded-lg p-3 bg-muted/50 flex items-start gap-3">
      <div className="flex-1 grid gap-2">
        <div className="flex items-center gap-2">
          <label className="text-detail font-medium text-gray-600 grid gap-1 flex-1">
            Name
            <Input
              value={entry.key}
              onChange={(e) => onChange({ key: e.target.value })}
              placeholder="argument_name"
              className="h-8"
            />
          </label>
          {showValueType && (
            <label className="text-detail font-medium text-gray-600 grid gap-1">
              Type
              <Select
                value={entry.valueType}
                onValueChange={(v) => onChange({ valueType: v as ValueType })}
              >
                <SelectTrigger className="h-8 w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="fixed_value">Fixed Value</SelectItem>
                  <SelectItem value="llm_value">LLM Prompt</SelectItem>
                </SelectContent>
              </Select>
            </label>
          )}
        </div>
        <label className="text-detail font-medium text-gray-600 grid gap-1">
          Value
          {entry.valueType === 'llm_value' ? (
            <Textarea
              value={entry.value}
              onChange={(e) => onChange({ value: e.target.value })}
              placeholder="Analysis key name"
              className="min-h-[80px]"
            />
          ) : (
            <Input
              value={entry.value}
              onChange={(e) => onChange({ value: e.target.value })}
              placeholder="Enter value"
              className="h-8"
            />
          )}
        </label>
      </div>
      <Button
        variant="ghost"
        size="sm"
        onClick={onRemove}
        className="h-8 w-8 p-0 flex-shrink-0 text-gray-400 hover:text-destructive"
      >
        <Trash2 className="w-3 h-3" />
      </Button>
    </div>
  )
}

function LlmOnlyArgumentRow({
  entry,
  onChange,
  onRemove,
}: {
  entry: VariableEntry
  onChange: (patch: Partial<VariableEntry>) => void
  onRemove: () => void
}) {
  return (
    <div className="border rounded-lg p-3 bg-muted/50 flex items-start gap-3">
      <div className="flex-1 grid gap-2">
        <label className="text-detail font-medium text-gray-600 grid gap-1">
          Field Name
          <Input
            value={entry.key}
            onChange={(e) => onChange({ key: e.target.value })}
            placeholder="e.g. customer_intent"
            className="h-8"
          />
        </label>
        <label className="text-detail font-medium text-gray-600 grid gap-1">
          LLM Prompt
          <Textarea
            value={entry.value}
            onChange={(e) => onChange({ value: e.target.value })}
            placeholder="Prompt for the LLM to extract this field"
            className="min-h-[80px]"
          />
        </label>
      </div>
      <Button
        variant="ghost"
        size="sm"
        onClick={onRemove}
        className="h-8 w-8 p-0 flex-shrink-0 text-gray-400 hover:text-destructive"
      >
        <Trash2 className="w-3 h-3" />
      </Button>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const KNOWN_START_KEYS = ['CampaignId'] as const
const KNOWN_END_OLOS_KEYS = ['CampaignId', 'DestCampaignId', 'CallbackDate', 'CallbackPhoneNumber'] as const

function configToVariables(config: Record<string, ArgumentConfig>, knownKeys: readonly string[]): VariableEntry[] {
  return Object.entries(config)
    .filter(([k]) => !knownKeys.includes(k))
    .map(([k, v]) => ({ id: crypto.randomUUID(), key: k, value: v.value, valueType: v.valueType }))
}

function variablesToConfig(variables: VariableEntry[]): Record<string, ArgumentConfig> {
  const out: Record<string, ArgumentConfig> = {}
  for (const v of variables) {
    if (!v.key.trim()) continue
    out[v.key] = { value: v.value, valueType: v.valueType, is_required: false }
  }
  return out
}

function getArg(
  config: Record<string, ArgumentConfig>,
  key: string,
  defaults?: Partial<ArgumentConfig>
): ArgumentConfig {
  return config[key] ?? {
    value: defaults?.value ?? '',
    valueType: defaults?.valueType ?? 'fixed_value',
    is_required: defaults?.is_required ?? false,
  }
}

// ---------------------------------------------------------------------------
// Start Call Card
// ---------------------------------------------------------------------------

interface CardHandle {
  save: () => void
}

const StartCallCard = forwardRef<CardHandle, { campaignId: number; existing?: IntegrationRow }>(
  function StartCallCard({ campaignId, existing }, ref) {
  const upsert = useUpsertIntegration()
  const remove = useDeleteIntegration()

  const [provider, setProvider] = useState<string>(existing?.integration_type ?? 'none')
  const config = (existing?.config ?? {}) as Record<string, ArgumentConfig>

  const [campaignIdVal, setCampaignIdVal] = useState(getArg(config, 'CampaignId', { is_required: true }).value)
  const [variables, setVariables] = useState<VariableEntry[]>(() => configToVariables(config, KNOWN_START_KEYS))

  useEffect(() => {
    if (existing) {
      setProvider(existing.integration_type ?? 'none')
      const c = (existing.config ?? {}) as Record<string, ArgumentConfig>
      setCampaignIdVal(getArg(c, 'CampaignId', { is_required: true }).value)
      setVariables(configToVariables(c, KNOWN_START_KEYS))
    } else {
      setProvider('none')
      setCampaignIdVal('')
      setVariables([])
    }
  }, [existing])

  const handleSave = () => {
    const cfg: Record<string, ArgumentConfig> = {
      CampaignId: { value: campaignIdVal, valueType: 'fixed_value', is_required: true },
      ...variablesToConfig(variables),
    }
    upsert.mutate(
      { campaignId, hook: 'start_call', integration_type: 'olos', config: cfg },
      {
        onSuccess: () => toast.success('Start call integration saved'),
        onError: (e) => toast.error(e.message),
      }
    )
  }

  const handleDelete = () => {
    remove.mutate(
      { campaignId, hook: 'start_call' },
      {
        onSuccess: () => toast.success('Start call integration removed'),
        onError: (e) => toast.error(e.message),
      }
    )
  }

  const handleProviderChange = (val: string) => {
    setProvider(val)
    if (val === 'none' && existing) handleDelete()
  }

  useImperativeHandle(ref, () => ({
    save: () => {
      if (provider === 'olos' && campaignIdVal.trim()) handleSave()
    }
  }))

  return (
    <Card>
      <CardHeader className="border-b py-2">
        <CardTitle className="text-body">Start Call Lead Info</CardTitle>
      </CardHeader>
      <CardContent className="p-4 grid gap-4">
        <label className="text-detail font-medium text-gray-600 grid gap-1">
          Integration Provider
          <Select value={provider} onValueChange={handleProviderChange}>
            <SelectTrigger className="h-8">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">None</SelectItem>
              <SelectItem value="intone_crm">Intone CRM</SelectItem>
              <SelectItem value="olos">Olos</SelectItem>
              <SelectItem value="custom_api" disabled>Custom API</SelectItem>
            </SelectContent>
          </Select>
        </label>

        {(provider === 'none' || provider === 'intone_crm') && (
          <p className="text-detail text-gray-500">Lead data loaded from Intone CRM (default)</p>
        )}

        {provider === 'olos' && (
          <>
            <ArgumentField
              label="Olos Campaign ID"
              required
              value={campaignIdVal}
              onValueChange={setCampaignIdVal}
              placeholder="e.g. 102"
            />

            {variables.map((v) => (
              <CustomArgumentRow
                key={v.id}
                entry={v}
                onChange={(patch) => setVariables((prev) => prev.map((x) => x.id === v.id ? { ...x, ...patch } : x))}
                onRemove={() => setVariables((prev) => prev.filter((x) => x.id !== v.id))}
              />
            ))}

            <Button
              variant="outline"
              onClick={() => setVariables((prev) => [...prev, { id: crypto.randomUUID(), key: '', value: '', valueType: 'fixed_value' }])}
              className="w-full sm:w-auto justify-self-start"
            >
              <Plus className="w-3 h-3" />
              Add Argument
            </Button>
          </>
        )}
      </CardContent>
    </Card>
  )
})

// ---------------------------------------------------------------------------
// End Call Card
// ---------------------------------------------------------------------------

const EndCallCard = forwardRef<CardHandle, { campaignId: number; existing?: IntegrationRow }>(
  function EndCallCard({ campaignId, existing }, ref) {
  const upsert = useUpsertIntegration()
  const remove = useDeleteIntegration()

  const existingType = existing?.integration_type ?? null
  const [provider, setProvider] = useState<string>(existingType ?? 'none')
  const config = (existing?.config ?? {}) as Record<string, ArgumentConfig>

  const [campaignIdVal, setCampaignIdVal] = useState(getArg(config, 'CampaignId', { is_required: true }).value)
  const [destCampaignId, setDestCampaignId] = useState(getArg(config, 'DestCampaignId').value)
  const [callbackDate, setCallbackDate] = useState(getArg(config, 'CallbackDate').value)
  const [callbackDateType, setCallbackDateType] = useState<ValueType>(getArg(config, 'CallbackDate').valueType)
  const [callbackPhone, setCallbackPhone] = useState(getArg(config, 'CallbackPhoneNumber').value)
  const [callbackPhoneType, setCallbackPhoneType] = useState<ValueType>(getArg(config, 'CallbackPhoneNumber').valueType)
  const [olosVariables, setOlosVariables] = useState<VariableEntry[]>(() =>
    existingType === 'olos' ? configToVariables(config, KNOWN_END_OLOS_KEYS) : []
  )

  const [intoneVariables, setIntoneVariables] = useState<VariableEntry[]>(() =>
    existingType === 'intone_crm' ? configToVariables(config, []) : []
  )

  useEffect(() => {
    if (existing) {
      const t = existing.integration_type ?? 'none'
      setProvider(t)
      const c = (existing.config ?? {}) as Record<string, ArgumentConfig>
      if (t === 'olos') {
        setCampaignIdVal(getArg(c, 'CampaignId', { is_required: true }).value)
        setDestCampaignId(getArg(c, 'DestCampaignId').value)
        setCallbackDate(getArg(c, 'CallbackDate').value)
        setCallbackDateType(getArg(c, 'CallbackDate').valueType)
        setCallbackPhone(getArg(c, 'CallbackPhoneNumber').value)
        setCallbackPhoneType(getArg(c, 'CallbackPhoneNumber').valueType)
        setOlosVariables(configToVariables(c, KNOWN_END_OLOS_KEYS))
      } else if (t === 'intone_crm') {
        setIntoneVariables(configToVariables(c, []))
      }
    } else {
      setProvider('none')
      setCampaignIdVal('')
      setDestCampaignId('')
      setCallbackDate('')
      setCallbackDateType('fixed_value')
      setCallbackPhone('')
      setCallbackPhoneType('fixed_value')
      setOlosVariables([])
      setIntoneVariables([])
    }
  }, [existing])

  const handleSaveOlos = () => {
    const cfg: Record<string, ArgumentConfig> = {
      CampaignId: { value: campaignIdVal, valueType: 'fixed_value', is_required: true },
    }
    if (destCampaignId.trim()) cfg.DestCampaignId = { value: destCampaignId, valueType: 'fixed_value', is_required: false }
    if (callbackDate.trim()) cfg.CallbackDate = { value: callbackDate, valueType: callbackDateType, is_required: false }
    if (callbackPhone.trim()) cfg.CallbackPhoneNumber = { value: callbackPhone, valueType: callbackPhoneType, is_required: false }
    Object.assign(cfg, variablesToConfig(olosVariables))

    upsert.mutate(
      { campaignId, hook: 'end_call', integration_type: 'olos', config: cfg },
      {
        onSuccess: () => toast.success('End call integration saved'),
        onError: (e) => toast.error(e.message),
      }
    )
  }

  const handleSaveIntone = () => {
    upsert.mutate(
      { campaignId, hook: 'end_call', integration_type: 'intone_crm', config: variablesToConfig(intoneVariables) },
      {
        onSuccess: () => toast.success('End call integration saved'),
        onError: (e) => toast.error(e.message),
      }
    )
  }

  const handleDelete = () => {
    remove.mutate(
      { campaignId, hook: 'end_call' },
      {
        onSuccess: () => toast.success('End call integration removed'),
        onError: (e) => toast.error(e.message),
      }
    )
  }

  const handleProviderChange = (val: string) => {
    setProvider(val)
    if (val === 'none' && existing) handleDelete()
  }

  const savedProvider = existing?.integration_type ?? 'none'
  const providerChanged = provider !== savedProvider

  useImperativeHandle(ref, () => ({
    save: () => {
      if (provider === 'olos' && campaignIdVal.trim()) handleSaveOlos()
      else if (provider === 'intone_crm' && (providerChanged || intoneVariables.length > 0)) handleSaveIntone()
    }
  }))

  return (
    <Card>
      <CardHeader className="border-b py-2">
        <CardTitle className="text-body">End Call Lead Update</CardTitle>
      </CardHeader>
      <CardContent className="p-4 grid gap-4">
        <label className="text-detail font-medium text-gray-600 grid gap-1">
          Integration Provider
          <Select value={provider} onValueChange={handleProviderChange}>
            <SelectTrigger className="h-8">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">None</SelectItem>
              <SelectItem value="intone_crm">Intone CRM</SelectItem>
              <SelectItem value="olos">Olos</SelectItem>
              <SelectItem value="custom_api" disabled>Custom API</SelectItem>
            </SelectContent>
          </Select>
        </label>

        {provider === 'none' && (
          <p className="text-detail text-gray-500">No end-call integration. Default behavior applies.</p>
        )}

        {/* Intone CRM — LLM-only args */}
        {provider === 'intone_crm' && (
          <>
            <p className="text-detail text-gray-500">
              Define fields to extract from the LLM analysis and send to Intone CRM after the call.
            </p>

            {intoneVariables.map((v) => (
              <LlmOnlyArgumentRow
                key={v.id}
                entry={v}
                onChange={(patch) => setIntoneVariables((prev) => prev.map((x) => x.id === v.id ? { ...x, ...patch } : x))}
                onRemove={() => setIntoneVariables((prev) => prev.filter((x) => x.id !== v.id))}
              />
            ))}

            <Button
              variant="outline"
              onClick={() => setIntoneVariables((prev) => [...prev, { id: crypto.randomUUID(), key: '', value: '', valueType: 'llm_value' }])}
              className="w-full sm:w-auto justify-self-start"
            >
              <Plus className="w-3 h-3" />
              Add Argument
            </Button>
          </>
        )}

        {/* Olos */}
        {provider === 'olos' && (
          <>
            <p className="text-detail text-gray-500">
              Disposition code and ASR transcript are resolved automatically from call data.
            </p>

            <ArgumentField
              label="Olos Campaign ID"
              required
              value={campaignIdVal}
              onValueChange={setCampaignIdVal}
              placeholder="e.g. 102"
            />

            <ArgumentField
              label="Dest Campaign ID"
              description="Destination campaign for callback routing"
              value={destCampaignId}
              onValueChange={setDestCampaignId}
              placeholder="e.g. 200"
            />

            <ArgumentField
              label="Callback Date"
              description="Date/time for scheduled callback"
              value={callbackDate}
              onValueChange={setCallbackDate}
              placeholder={callbackDateType === 'llm_value' ? 'e.g. callback_date' : 'e.g. 2026-04-01 14:00'}
              valueType={callbackDateType}
              onValueTypeChange={setCallbackDateType}
            />

            <ArgumentField
              label="Callback Phone Number"
              description="Phone number for callback"
              value={callbackPhone}
              onValueChange={setCallbackPhone}
              placeholder={callbackPhoneType === 'llm_value' ? 'e.g. callback_phone' : 'e.g. +5511999999999'}
              valueType={callbackPhoneType}
              onValueTypeChange={setCallbackPhoneType}
            />

          </>
        )}
      </CardContent>
    </Card>
  )
})

// ---------------------------------------------------------------------------
// Public component
// ---------------------------------------------------------------------------

export interface IntegrationCardsHandle {
  save: () => void
}

export const IntegrationCards = forwardRef<IntegrationCardsHandle, { campaignId: number }>(
  function IntegrationCards({ campaignId }, ref) {
    const { data: integrations = [] } = useCampaignIntegrations(campaignId)
    const startRef = useRef<CardHandle>(null)
    const endRef = useRef<CardHandle>(null)

    const startCall = integrations.find((i) => i.hook === 'start_call')
    const endCall = integrations.find((i) => i.hook === 'end_call')

    useImperativeHandle(ref, () => ({
      save: () => {
        startRef.current?.save()
        endRef.current?.save()
      }
    }))

    if (!campaignId) return null

    return (
      <div className="grid gap-4 grid-cols-[repeat(auto-fit,_minmax(560px,_1fr))]">
        <StartCallCard ref={startRef} campaignId={campaignId} existing={startCall} />
        <EndCallCard ref={endRef} campaignId={campaignId} existing={endCall} />
      </div>
    )
  }
)
